/**
 * Analyzer: Day Event Citations (Pro)
 *
 * Pro-tier deep historical analysis of event-day performance. For each event
 * type tagged on today, aggregates the user's complete historical track
 * record and cites specifics — sample size, win rate on event-days vs a true
 * non-event-day baseline, worst individual event-day, avg P&L.
 *
 * This is the wired version of the headline Pro feature advertised in
 * CoachAboutModal — the "CPI days: you're −$240 avg over 6 months, 28% WR
 * vs 52% baseline" pitch.
 *
 * ── Relationship to `dayNewsEventImpact` (Free) ─────────────────────────────
 * Free version nudges: "today beat your typical CPI day" — one-number relative
 * comparison, fires with ≥2 prior same-tag days.
 * Pro version cites: "your CPI-day track record is X over N prints — here's
 * the baseline, here's the worst day, here's the trend" — needs ≥3 prior
 * days and adds the non-event baseline lens. Both can fire; they answer
 * different questions.
 *
 * ── Silence rules ───────────────────────────────────────────────────────────
 *   - No event tags on today → null (nothing to cite against)
 *   - No historical journal entries → null
 *   - Every event tag has <MIN_SAMPLE prior tagged days → null
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types';

export const key = 'dayEventCitations';
export const tier = 'pro';

// Minimum prior event-days before we emit a citation for that tag.
// Below this, sample is too small for a confident pattern — skip silently.
const MIN_SAMPLE = 3;

// Cap observations per day so multiple tagged events don't flood the feed.
// We keep the two biggest-magnitude signals — whichever event types matter
// most to today's session.
const MAX_EVENTS_TO_CITE = 2;

// Threshold (USD) for classifying an event-day pattern as "significant".
// Below this magnitude we emit an INFO observation instead of WARNING/SUCCESS.
const SIGNIFICANCE_USD = 50;

const fmt = (n) => `${n >= 0 ? '+' : '−'}$${Math.abs(n).toFixed(0)}`;

export const analyze = (ctx) => {
  const { day, allJournalEntries = {}, allTrades = [], getTradeNetPnL, getAccountById } = ctx;
  if (!day || !day.core) return null;
  // Live-trade gate: skip when no trades have closed today. v7.5.14 contract
  // extended to day-scope; see dayUtils.js for the closed-only core stats.
  if (day.core.closedTotal === 0) return null;

  const todayTags = Array.isArray(day.journalEntry?.eventTags) ? day.journalEntry.eventTags : [];
  if (todayTags.length === 0) return null;

  const pastEntryDates = Object.keys(allJournalEntries).filter((d) => d !== day.date);
  if (pastEntryDates.length === 0) return null;

  const netFor = (t) => {
    const acc = getAccountById ? getAccountById(t.accountId) : null;
    const r = getTradeNetPnL ? getTradeNetPnL(t, acc) : { netPnl: parseFloat(t.pnl) || 0 };
    return isFinite(r?.netPnl) ? r.netPnl : 0;
  };

  // ── Per-day aggregates (past only) ──────────────────────────────────────
  // Build once, reuse for event-day and baseline lookups. Map date → { pnl, trades, wins }.
  const dayAggregates = {};
  const datesWithTrades = new Set();
  for (const t of allTrades) {
    if (!t.date || t.date === day.date) continue;
    datesWithTrades.add(t.date);
    if (!dayAggregates[t.date]) dayAggregates[t.date] = { pnl: 0, trades: 0, wins: 0 };
    const n = netFor(t);
    dayAggregates[t.date].pnl += n;
    dayAggregates[t.date].trades++;
    if (n > 0) dayAggregates[t.date].wins++;
  }

  // ── Reverse index: tag → past dates tagged with that event ──────────────
  // Tags are matched case-insensitively so "CPI" and "cpi" don't fragment
  // the sample. We normalize to the first casing seen for display.
  const datesByTag = {};
  const tagCanonical = {}; // lowercase → original casing for display
  for (const d of pastEntryDates) {
    const entry = allJournalEntries[d];
    const tags = Array.isArray(entry?.eventTags) ? entry.eventTags : [];
    for (const tag of tags) {
      const lc = tag.toLowerCase();
      if (!datesByTag[lc]) datesByTag[lc] = [];
      datesByTag[lc].push(d);
      if (!tagCanonical[lc]) tagCanonical[lc] = tag;
    }
  }

  // ── Non-event-day baseline ──────────────────────────────────────────────
  // True baseline: past trading days that had NO event tags at all. This is
  // the apples-to-apples "normal day" for the user.
  const allTaggedDates = new Set();
  Object.values(datesByTag).forEach((dates) => dates.forEach((d) => allTaggedDates.add(d)));
  const nonEventDates = [...datesWithTrades].filter((d) => !allTaggedDates.has(d));
  const baselineDays = nonEventDates.map((d) => dayAggregates[d]).filter(Boolean);

  const baselineAvg = baselineDays.length > 0
    ? baselineDays.reduce((s, a) => s + a.pnl, 0) / baselineDays.length
    : null;
  const baselineTradeCount = baselineDays.reduce((s, a) => s + a.trades, 0);
  const baselineWinCount   = baselineDays.reduce((s, a) => s + a.wins, 0);
  const baselineWR = baselineTradeCount > 0
    ? (baselineWinCount / baselineTradeCount) * 100
    : null;

  // ── Citation per today-tag ──────────────────────────────────────────────
  const citations = [];
  for (const todayTag of todayTags) {
    const lc = todayTag.toLowerCase();
    const historyDates = (datesByTag[lc] || []).filter((d) => dayAggregates[d]);
    if (historyDates.length < MIN_SAMPLE) continue;

    const aggs = historyDates.map((d) => ({ date: d, ...dayAggregates[d] }));
    const totalPnl = aggs.reduce((s, a) => s + a.pnl, 0);
    const totalTrades = aggs.reduce((s, a) => s + a.trades, 0);
    const totalWins = aggs.reduce((s, a) => s + a.wins, 0);
    const avgPnl = totalPnl / aggs.length;
    const eventWR = totalTrades > 0 ? (totalWins / totalTrades) * 100 : 0;

    const sortedByPnl = [...aggs].sort((a, b) => a.pnl - b.pnl);
    const worst = sortedByPnl[0];
    const best  = sortedByPnl[sortedByPnl.length - 1];
    const winDays = aggs.filter((a) => a.pnl > 0).length;

    citations.push({
      tag: tagCanonical[lc] || todayTag,
      sample: aggs.length,
      avgPnl,
      eventWR,
      worst,
      best,
      winDays,
      totalTrades,
      totalWins,
    });
  }

  if (citations.length === 0) return null;

  // ── Rank by magnitude, cap, emit ────────────────────────────────────────
  // Biggest-signal events first — a −$300 avg event beats a +$20 avg event
  // for user attention even if the smaller one is technically positive.
  citations.sort((a, b) => Math.abs(b.avgPnl) - Math.abs(a.avgPnl));
  const topCitations = citations.slice(0, MAX_EVENTS_TO_CITE);

  const out = [];
  for (const c of topCitations) {
    const negative = c.avgPnl < -SIGNIFICANCE_USD && c.sample >= 4;
    const positive = c.avgPnl > SIGNIFICANCE_USD && c.sample >= 4;

    let severity = SEVERITY.INFO;
    let priority = 70;
    if (negative) {
      severity = SEVERITY.WARNING;
      priority = 82;
    } else if (positive) {
      severity = SEVERITY.SUCCESS;
      priority = 68;
    }

    const title = negative
      ? `${c.tag} days: net-negative track record`
      : positive
        ? `${c.tag} days: your edge holds up`
        : `${c.tag} days: your historical read`;

    const baselineStr = baselineAvg !== null
      ? ` (baseline: ${fmt(baselineAvg)}${baselineWR !== null ? ` @ ${baselineWR.toFixed(0)}% WR` : ''})`
      : '';

    const detail = `${c.sample} prior ${c.tag} day${c.sample > 1 ? 's' : ''}: ${fmt(c.avgPnl)} avg @ ${c.eventWR.toFixed(0)}% WR${baselineStr}. Worst: ${fmt(c.worst.pnl)} on ${c.worst.date}.`;

    let action;
    if (negative) {
      action = `Historically these chop you. Consider sizing down or a 10-min no-fly zone around releases.`;
    } else if (positive) {
      action = `Your playbook works here — stick to the size + setup that's been working.`;
    } else if (c.sample < 5) {
      action = `Small sample — take the pattern loosely, and log how today compares.`;
    } else {
      action = `Net-flat on these historically. Volatility is real even when the average is neutral.`;
    }

    out.push(makeObservation({
      id: `${key}:${day.date}:${c.tag}`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity,
      category: CATEGORY.VARIANCE,
      title,
      detail,
      action,
      evidence: {
        eventTag: c.tag,
        sampleSize: c.sample,
        avgEventPnl: c.avgPnl,
        avgBaselinePnl: baselineAvg,
        eventWR: c.eventWR,
        baselineWR,
        worstDay: c.worst,
        bestDay: c.best,
        winDayCount: c.winDays,
      },
      metric: {
        label: `${c.tag} avg`,
        value: fmt(c.avgPnl),
        unit: '',
        baseline: baselineAvg !== null ? fmt(baselineAvg) : null,
      },
      // Confidence scales with sample — 3 days → 0.68, 10 days → 0.95 ceiling.
      confidence: Math.min(0.95, 0.5 + c.sample * 0.06),
      priority,
    }));
  }

  return out.length > 0 ? out : null;
};
